import React from "react";
import { useEffect, useState } from "react";
import marketdata from "../data/Services/marketdata";
import { useAuthStore } from "../store/authStore";
import { useThemeStore } from "../store/themeStore";
import { isDarkTheme } from "../config/themes";
import { useConnectivityCheck } from "../hooks/useConnectivityCheck";
import "./OfflineOverlay.css";

const OfflineOverlay = () => {
  const [isOffline, setIsOffline] = useState(false);
  const [disconnectionStartTime, setDisconnectionStartTime] = useState(null);
  const [hasBeenConnected, setHasBeenConnected] = useState(false);
  const { browserOnline, connectivityCheckFailed } = useConnectivityCheck();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const theme = useThemeStore((state) => state.theme);

  useEffect(() => {
    // Função para atualizar o estado offline
    const updateOfflineState = () => {
      const isConnected = marketdata.state === "online";
      const isDisconnected = marketdata.state === "offline";

      // Also check SignalR connection state if available
      // SignalR states: 0=Connecting, 1=Connected, 2=Reconnecting, 6=Disconnected
      const signalRConnection = marketdata.signalRConnection;
      let signalRState = null;
      let isSignalRDisconnected = false;
      let isSignalRConnecting = false;

      if (signalRConnection) {
        // Try different ways to get the connection state
        signalRState =
          signalRConnection.state ||
          signalRConnection.connectionState ||
          signalRConnection.connection?.state;

        // Check for disconnected state
        isSignalRDisconnected =
          signalRState === "Disconnected" ||
          signalRState === 6 ||
          signalRState === "disconnected" ||
          !signalRConnection.connectionId; // No connection ID means disconnected

        // Check for connecting/reconnecting states
        isSignalRConnecting =
          signalRState === "Connecting" ||
          signalRState === "Reconnecting" ||
          signalRState === 0 ||
          signalRState === 2 ||
          signalRState === "connecting" ||
          signalRState === "reconnecting";
      }

      // Determine comprehensive online status
      // Show overlay when either server OR internet is offline
      const currentlyOffline =
        isAuthenticated &&
        (isDisconnected ||
          isSignalRDisconnected ||
          (!isSignalRConnecting && !isConnected && !isFullyConnected) ||
          !browserOnline ||
          connectivityCheckFailed);

      // Track if user has ever been connected (track connection state regardless of auth)
      const isFullyConnected =
        isConnected ||
        signalRState === "Connected" ||
        signalRState === 1 ||
        signalRState === "connected";
      if (isFullyConnected && browserOnline && !hasBeenConnected) {
        setHasBeenConnected(true);
        console.log("🔗 Usuário conectado pela primeira vez");
      }

      // Track disconnection start time (when authenticated and either server OR internet is offline)
      if (currentlyOffline && !disconnectionStartTime) {
        setDisconnectionStartTime(Date.now());
        console.log("⏰ Iniciando contagem de desconexão");
      } else if (!currentlyOffline) {
        if (disconnectionStartTime) {
          console.log("🔄 Conexão restaurada, resetando timer");
        }
        setDisconnectionStartTime(null);
      }

      // Only show overlay if:
      // 1. User is authenticated
      // 2. Currently offline (either marketdata offline OR browser offline)
      // 3. 10 seconds have passed since disconnection
      const shouldShowOverlay =
        currentlyOffline &&
        disconnectionStartTime &&
        Date.now() - disconnectionStartTime >= 10000;

      setIsOffline(shouldShowOverlay);
    };

    // Verificar estado inicial
    updateOfflineState();

    // Criar um intervalo para verificar o estado periodicamente
    const intervalId = setInterval(updateOfflineState, 1000);

    // Cleanup
    return () => {
      clearInterval(intervalId);
    };
  }, [
    isAuthenticated,
    disconnectionStartTime,
    hasBeenConnected,
    browserOnline,
    connectivityCheckFailed,
  ]); // Re-executar quando o status de autenticação mudar

  // Não renderizar nada se não estiver offline ou não autenticado
  if (!isOffline) {
    return null;
  }

  return (
    <div
      className={`offline-overlay ${isDarkTheme(theme) ? "dark-theme" : "light-theme"}`}
    >
      <div className="offline-content">
        <div className="offline-icon">📡</div>
        <h2>Conexão Perdida</h2>
        <p>Reconectando ao servidor de dados...</p>
        <div className="offline-spinner"></div>
      </div>
    </div>
  );
};

export default OfflineOverlay;
